<template>
  <div
    class="input-wrapper relative z-10 w-full rounded-card border border-line bg-chrome py-3 [backdrop-filter:var(--glass-blur)]
           [container-type:inline-size]"
    ref="wrapper"
    :class="wrapperComponentClass"
  >
    <input class="hidden" ref="input" type="file" multiple :accept="imgExtensions.join(',')" @change="onInputFileChange">
    <div class="quill-container h-full min-h-10.5 w-full border-0 px-3 pb-9 transition-[padding-bottom] duration-400"
      ref="container" :class="editorContainerClass" @click.stop='() => true'
      @click="emitClick()" @mouseup="selectQuillText"></div>
    <div class="quill-toolbar absolute bottom-2 left-2 flex items-center gap-0.5" ref="toolbar" v-show="isShowFloatToolbar">
      <SimpleButton v-for="f in formatButtons" :key="f.title"
        :class="{ 'hide-mob': f.hideOnNarrow, 'hide-narrowest': f.hideOnNarrowest }"
        variant="ghost"
        size="icon"
        :pressed="f.pressed()"
        :title="f.title"
        @click="f.run()"
      >
        <component :is="f.icon" class="size-4" />
      </SimpleButton>
    </div>
    <PromptDialog
      v-if="linkPromptRange"
      :title="$t('common.Add_link')"
      :textLabel="$t('common.Link_text')"
      :textPlaceholder="$t('common.Enter_link_text')"
      :initialText="linkPromptText"
      :urlLabel="$t('common.Enter_a_URL')"
      :placeholder="$t('common.Enter_a_URL')"
      :cancelText="$t('common.Cancel')"
      :confirmText="$t('common.Add')"
      @confirm="applyLink"
      @cancel="cancelLink"
    />
    <RecordingPopup v-if="showRecordingPopup" ref="recordingPopupRef" />

    <div class="quill-float-btn absolute right-2 bottom-2 flex items-center justify-end gap-0.5">
      <SimpleButton variant="ghost" size="icon" :pressed="!isFormatClick" @click="isFormatClick = !isFormatClick">
        <i class="tr tr-show-format flex size-4 items-center"></i>
      </SimpleButton>
      <SimpleButton variant="ghost" size="icon" v-if="mentionButton" @click="onMentionClick">
        <IconAtSymbol16Solid class="size-4" />
      </SimpleButton>
      <SimpleButton v-if="attachmentButton" variant="ghost" size="icon" @click="$refs.input.value = ''; $refs.input.click();">
        <IconPaperClip16Solid class="w-4 h-4 m-1" />
      </SimpleButton>
      <SimpleButton
        v-if="recordingButton"
        class="recording-popup-trigger hide-mob"
        variant="ghost"
        size="icon"
        :title="recordingActive ? $t('recording.Stop_recording') : $t('task.Record_screen')"
        :pressed="recordingActive || showRecordingPopup"
        @click="toggleRecordingPopup"
        :disabled="showRecordingPopup && !recordingPopupRef?.recordingInitialized"
      >
        <span class="flex size-4 items-center justify-center" v-if="showRecordingPopup && !recordingPopupRef?.recordingInitialized">
          <span class="inline-loader"></span>
        </span>
        <IconVideoCamera16Solid v-else class="size-4"></IconVideoCamera16Solid>
      </SimpleButton>
      <SimpleButton class="hide-mob" variant="ghost" size="icon" :pressed="showEmojii" @mousedown.prevent @click="showEmojii = !showEmojii; if (!recordingActive && recordingPopupRef?.recordingInitialized) showRecordingPopup = false;">
        <IconFaceSmile16Solid class="size-4" />
      </SimpleButton>
      <SimpleButton variant="accent" size="icon" @click.stop="doSubmit()">
        <IconPaperAirplane16Solid class="size-4" />
      </SimpleButton>
    </div>
    <PreviewImageUploader v-if="attachments.length" :initPreviewImage="attachments" :limitPreview="MAX_ATTACHED_IMAGES" ref="preview"
      @mount="mountPreview" @upload="imageArray.push($event.image)" @remove="imageArray.splice($event.removeId, 1)"
      @reorder="imageArray = $event" @empty="previewEmpty" @height="previewHeight = $event"
      @error="onPreviewError"></PreviewImageUploader>
    <transition enter-active-class="fadeIn" leave-active-class="fadeOut">
      <Picker v-if="showEmojii" autoFocus class="emoji-picker" :class="emojiPickerBelow && 'top-full! bottom-auto!'" :data="getEmojiIndex()" set="apple" @select="onSelectEmojii">
      </Picker>
    </transition>
  </div>
</template>


<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick, onUnmounted, getCurrentInstance } from 'vue';
import Quill from 'quill';
import { registerMDShortcuts } from '@/components/elements/quill/markdown-shortcuts.js';
import { MarkdownToQuill } from 'md-to-quill-delta';
import "quill-mention/autoregister";
import MagicUrl from 'quill-magic-url';
import { Picker } from 'emoji-mart-vue-fast/src';
import { getEmojiIndex } from '@/utils/emojiIndex';
import PreviewImageUploader from '@/components/elements/PreviewImageUploader';
import { MAX_ATTACHED_IMAGES, MAX_IMAGES_ALERT_ID } from '@/utils/constantHolder';
import RecordingPopup from '@/components/elements/RecordingPopup';
import eventBus from '@/eventBus';
import { useStore } from 'vuex';
// import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import 'emoji-mart-vue-fast/css/emoji-mart.css';
import hljs from 'highlight.js';
// store imported above
import md5 from 'md5';
import { IconAtSymbol16Solid, IconBold16Solid, IconCodeBracket16Solid, IconCodeBracketSquare16Solid, IconFaceSmile16Solid, IconItalic16Solid, IconLink16Solid, IconListBullet16Solid, IconNumberedList16Solid, IconPaperAirplane16Solid, IconPaperClip16Solid, IconVideoCamera16Solid } from '@iconify-prerendered/vue-heroicons';
import { IconBlockquote } from '@iconify-prerendered/vue-tabler';
import SimpleButton from './SimpleButton.vue';
import PromptDialog from './PromptDialog.vue';
// outside-click handling moved to parent (EditorInput)
const { proxy } = getCurrentInstance();


const recordingPopupRef = ref(null);

function toggleRecordingPopup() {
  if (recordingActive.value) { 
    eventBus.$emit('callStopRecordingHandler');
  } else {  
    showRecordingPopup.value = !showRecordingPopup.value;
    showEmojii.value = false;
    quill.getModule('mention').hideMentionList();
  }
}

function createUserAvatarNode(user, active, noBorder = false) {
  // Create container div (like <div class="userAvatar">)
  const container = document.createElement('div');
  container.classList.add('userAvatar');
  if (active && !noBorder) container.classList.add('active');
  container.style.display = 'flex';
  container.style.width = '1.5rem';
  container.style.height = '1.5rem';
  container.style.flexShrink = '0';
  container.style.cursor = 'pointer';
  container.style.position = 'relative';

  // Helper: color array (same as Vue component)
  const colors = [
    '#1abc9c', '#2ecc71', '#3498db', '#9b59b6', '#34495e', '#16a085', '#27ae60', '#2980b9', '#8e44ad',
    '#2c3e50', '#f1c40f', '#e67e22', '#e74c3c', '#95a5a6', '#f39c12', '#d35400', '#c0392b', '#bdc3c7',
    '#7f8c8d', '#9400D3', '#D2B48C', '#0000CD',
  ];

  // Check if user has valid avatarFileName
  const hasAvatar = user?.avatarFileName;

  if (hasAvatar) {
    const img = document.createElement('img');
    img.className = active && !noBorder ? 'active' : '';
    img.crossOrigin = 'anonymous';
    img.title = user.username || user.name || '';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.borderRadius = '50%';

    // Compute baseUrl like Vue computed property
    let baseUrl = '';
    if (/\.(png|jpeg|jpg)$/i.test(user.avatarFileName)) {
      baseUrl = user.avatarFileName;
    } else {
      baseUrl = `${proxy.s3base || ''}/useravatars/${user.id}/${user.avatarFileName}.png`;
    }
    img.src = baseUrl;

    container.appendChild(img);
  } else {
    // Render initials with colored background
    const initialsDiv = document.createElement('div');
    initialsDiv.classList.add('letterAvatar');
    if (active && !noBorder) initialsDiv.classList.add('active');
    initialsDiv.style.display = 'flex';
    initialsDiv.style.justifyContent = 'center';
    initialsDiv.style.alignItems = 'center';
    initialsDiv.style.width = '100%';
    initialsDiv.style.height = '100%';
    initialsDiv.style.borderRadius = '50%';

    // Compute initials
    let name = (user && (user.username || user.name)) || '?';
    const nameSplit = name.split(' ');
    const initials = nameSplit[0].charAt(0).toUpperCase()
      + (nameSplit.length >= 2 ? nameSplit[1].charAt(0).toUpperCase() : '');

    // Compute bgColor from md5 hash char code
    let charIndex = 0;
    if (user && user.id) {
      const hash = md5(user.id.toString());
      charIndex = hash.charCodeAt(0);
    }
    const bgColor = colors[charIndex % colors.length] || '#aaa';

    initialsDiv.style.backgroundColor = bgColor;
    initialsDiv.style.color = 'white';
    initialsDiv.style.fontWeight = 'bold';
    initialsDiv.style.fontSize = 'var(--fs-body)';
    initialsDiv.textContent = initials;

    container.appendChild(initialsDiv);
  }
  const regCompleted =
    (user.userWorkspace && user.userWorkspace.registrationCompleted) ||
    (user.userWorkspaces && user.userWorkspaces.registrationCompleted);
  if (
    user &&
    user.email &&
    !regCompleted
  ) {
    const pendingDiv = document.createElement('div');
    pendingDiv.classList.add('pending');
    pendingDiv.title = proxy.$t('ui.editor_sign_up_not_confirmed');

    const icon = document.createElement('i');
    icon.classList.add('tr', 'tr-timer-manual-time');

    pendingDiv.appendChild(icon);
    // Style pendingDiv like in your CSS or add class (you should have .pending styles available)
    pendingDiv.style.position = 'absolute';
    pendingDiv.style.bottom = '-0.125rem';
    pendingDiv.style.right = '-0.125rem';
    pendingDiv.style.color = '#fff'; // or your $compBg variable color
    pendingDiv.style.opacity = '0.8';
    pendingDiv.style.fontWeight = 'bold';
    pendingDiv.style.width = '0.625rem';
    pendingDiv.style.height = '0.625rem';
    pendingDiv.style.fontSize = 'var(--fs-unit)';
    pendingDiv.style.display = 'flex';
    pendingDiv.style.alignItems = 'center';
    pendingDiv.style.justifyContent = 'center';

    container.appendChild(pendingDiv);
  }

  return container;
}


if (!window.QUILL_MODULES_REGISTERED) {
  window.QUILL_MODULES_REGISTERED = true;

  // Here we disable Quill's default clipboard, because we are catching paste events in other components by document.onpaste
  const Clipboard = Quill.import('modules/clipboard');
  class PotentiallyDisabledClipboard extends Clipboard {

    constructor(quill, options) {
      super(quill, options);
      this.quill.root.addEventListener('paste', this.onCapturePasteCustom.bind(this));
    }

    onCapturePaste(e) {
      e.preventDefault();
    }
    onCapturePasteCustom(e) {
      // only if not a text pasted
      if (e.clipboardData && e.clipboardData.files && e.clipboardData.files.length) {
        // prevent default paste behavior
        e.preventDefault();
        return;
      } else {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        const selection = this.quill.getSelection();

        if (selection) {
          const pasteLength = text.length;

          // If there's a selection, replace it with the pasted text
          this.quill.deleteText(selection.index, selection.length, 'user');
          this.quill.insertText(selection.index, text, 'user');

          // Move the cursor to the end of the pasted text
          this.quill.setSelection(selection.index + pasteLength, 0, 'silent');

          // Trigger magic-url detection by simulating the exact event magic-url expects
          setTimeout(() => {
            const range = this.quill.getSelection();
            if (range) {
              // Add a space with 'user' source (not 'silent') to trigger magic-url
              this.quill.insertText(range.index, ' ', 'user');
              // Remove the space immediately
              this.quill.deleteText(range.index, 1, 'user');
              // Reset cursor position
              this.quill.setSelection(range.index, 0, 'silent');
            }
          }, 10);
        }
      }
    }
  }

  Quill.register('modules/clipboard', PotentiallyDisabledClipboard);
  Quill.register('modules/magicUrl', MagicUrl);
}
registerMDShortcuts();


const store = useStore();

const getUser = computed(() => store.getters['user/getUser']);
const project = computed(() => store.state.project.project);


const props = defineProps({
  title: String,
  value: { type: [String, Object], default: '' },
  dontSubmitOnEnter: { type: Boolean, default: false },
  tagList: { type: Array, default: () => [] },
  initImgArray: { type: Array, default: () => [] },
  reversedTag: { type: Boolean, default: true },
  maxHeight: { type: String, default: '35vw' },
  neverScroll: { type: Boolean, default: false },
  focusOn: { type: Boolean, default: true },
  previewImage: { type: FileList, default: null },
  isHideFormat: { type: Boolean, default: false },
  typeOfEditor: { type: String, default: 'main' },
  allowSubmit: { type: Boolean, default: true },
  // submitOnFocusOut is deprecated in favor of confirm popup on outside click
  submitOnFocusOut: { type: Boolean, default: false },
  imgExtensions: { type: Array, default: () => ['.png', '.jpg', '.jpeg', '.gif', '.doc', '.pdf', '.txt'] },
  mentionButton: { type: Boolean, default: true },
  attachmentButton: { type: Boolean, default: true },
  recordingButton: { type: Boolean, default: true },
  emojiPickerBelow: { type: Boolean, default: false },
  notFocusOnInit: { type: Boolean, default: false },
  alwaysOpen: { type: Boolean, default: false },
});
const emit = defineEmits([
  'input', 'changed', 'selection', 'focus', 'clicked',
  'submit', 'submitWithAttach',
  'commentAttachImage', 'opened'
]);

// Refs
const wrapper = ref(null);
const input = ref(null);
const container = ref(null);
const toolbar = ref(null);
const preview = ref(null);

// State
let quill = null;
const options = ref({});
const converter = ref(null);
const Delta = ref(null);
const selectEvent = ref(null);
const toolbarData = reactive({ width: 0, height: 0 });
const imageArray = ref([]);
const attachments = ref([]);
const showEmojii = ref(false);
const isMentionListShow = ref(false);
const showRecordingPopup = ref(false);
const recordingActive = computed(() => store.state.system.recordingActive);
const selectionPos = ref(null);
const text = ref('');
const pLCont = ref(0);
let convertValue, textInit;
// Track current formats at selection/caret for reactive toolbar state
const currentFormats = ref({});
const linkPromptRange = ref(null);
const linkPromptText = ref('');
// removed unsaved-changes prompt state (managed by parent EditorInput)


function recordigFinished(file) {
  if (file) {
    attachments.value = [...attachments.value, file];
  }
  showRecordingPopup.value = false;
}

// Computed
const isSelectionText = computed(() => Boolean(selectionPos.value && selectionPos.value.length));
const isFormatClick = ref(props.isHideFormat);
watch(isFormatClick, handleFormatToggle);
const isShowFloatToolbar = computed(() =>
  !isFormatClick.value ? true : isSelectionText.value
);
// toolbar buttons — `pressed` is read during render, so it stays reactive
const formatButtons = [
  { title: 'ctrl + i', icon: IconItalic16Solid, pressed: () => hasFormat('italic'), run: () => toggleFormat('italic') },
  { title: 'ctrl + b', icon: IconBold16Solid, pressed: () => hasFormat('bold'), run: () => toggleFormat('bold') },
  { title: 'ctrl + shift + c', icon: IconCodeBracket16Solid, pressed: () => hasFormat('code'), run: () => toggleFormat('code'), hideOnNarrowest: true },
  { title: 'select text and ctrl + shift + l', icon: IconLink16Solid, pressed: () => hasFormat('link'), run: () => toggleLink() },
  { title: 'ctrl + shift + 8', icon: IconListBullet16Solid, pressed: () => hasFormat('list', 'bullet'), run: () => toggleList('bullet') },
  { title: 'ctrl + shift + 7', icon: IconNumberedList16Solid, pressed: () => hasFormat('list', 'ordered'), run: () => toggleList('ordered'), hideOnNarrowest: true },
  { title: 'ctrl + shift + 9', icon: IconBlockquote, pressed: () => hasFormat('blockquote'), run: () => toggleFormat('blockquote'), hideOnNarrow: true },
  { title: 'ctrl + {', icon: IconCodeBracketSquare16Solid, pressed: () => hasFormat('code-block'), run: () => toggleFormat('code-block'), hideOnNarrow: true },
];

const editorContainerClass = computed(() => 'ql-container ql-snow');
const wrapperComponentClass = computed(() => ({
  'input-wrapper-format-hide': isFormatClick.value,
  'input-wrapper-format-show': !isFormatClick.value,

  'input-wrapper-show-attachments-line': attachments.value.length,
  'input-wrapper-not-show-preview': !attachments.value.length,
}));
const suggestPeopleList = computed(() =>
  props.tagList.map(person =>
    typeof person === 'object'
      ? { ...person, value: person.username }
      : null
  ).filter(Boolean)
);
const userIsActive = (userId) => {
  return project.value.activeTrackUserIds
    && project.value.activeTrackUserIds.some(id => id === userId);
}


// onClickOutside is no longer responsible for closing and prompts; managed in EditorInput

// Watchers
watch(() => props.previewImage, value => {
  if (value?.length) {
    attachments.value = Array.from(value);
  }
});
watch(() => props.initImgArray, (arr, oldArr) => {
  if (arr.length && !oldArr.length) {
    arr.forEach(img => {
      quill.insertEmbed(0, 'image', img);
      quill.insertText(0, '\n');
    });
  }
});
const isQuillFocused = ref(props.focusOn && !props.notFocusOnInit);
const mentionFilterString = ref('');


function quillKeyDownHandler(e) {
  if (e.key === 'Escape') {
    e.stopPropagation();
  }
}


// Setup on mount
onMounted(async () => {
  Delta.value = Quill.import('delta');
  converter.value = new MarkdownToQuill({ debug: false });
  // Markdown -> Delta
  if (typeof props.value === 'string') {
    convertValue = converter.value.convert(props.value);
  }
  // Configure options
  options.value = {
    bounds: container.value,
    placeholder: props.title || 'Type...',

    modules: {

      clipboard: {
        onCapturePaste: (e) => {
        },
      },
      toolbar: toolbar.value,
      keyboard: {
        bindings: addKeyBindings()
      },
      markdownShortcuts: {},
      magicUrl: true,
      mention: {
        mentionDenotationChars: ['@'],
        offsetLeft: 0,
        offsetTop: 15,
        allowedChars: /^.*$/,
        defaultMenuOrientation: props.reversedTag ? 'top' : 'bottom',
        fixMentionsToQuill: true,
        onOpen: () => {
          isMentionListShow.value = true;
          if (!recordingActive.value && recordingPopupRef.value?.recordingInitialized) showRecordingPopup.value = false;
        },
        onClose: () => isMentionListShow.value = false,
        source(searchTerm, renderList) {
          mentionFilterString.value = searchTerm;
          const matches = suggestPeopleList.value.filter(p =>
            p.value.toLowerCase().includes(searchTerm.toLowerCase())
          );
          // if match starts with mentionFilterString, then put it first
          matches.sort((a, b) => {
            const aStartsWith = a.value.toLowerCase().startsWith(mentionFilterString.value.toLowerCase());
            const bStartsWith = b.value.toLowerCase().startsWith(mentionFilterString.value.toLowerCase());

            // If one starts with filter and other doesn't, prioritize the one that starts
            if (aStartsWith && !bStartsWith) return -1;
            if (!aStartsWith && bStartsWith) return 1;

            // If both start with filter OR both don't start with filter, sort by length (shortest first)
            if (a.value.length < b.value.length) return -1;
            if (a.value.length > b.value.length) return 1;

            // If same length, sort alphabetically
            return a.value.localeCompare(b.value);
          });
          renderList(matches);
        },
        renderItem: (data) => {
          const div = document.createElement("div");
          div.innerHTML = `
            ${createUserAvatarNode(getUser.value(data.id), userIsActive(data.id)).outerHTML}
            <span>${data.username}</span>
          `
          div.classList.add('mention-item');
          div.classList.add('text-text');
          return div;
        },
      },
    },
    theme: 'snow',
  };

  eventBus.$on('drop-files', files => {
    attachments.value = Array.from(files);
  });
  quill = new Quill(container.value, options.value);
  // quill.root.addEventListener('keydown', quillKeyDownHandler);
  window.qq = quill;

  // Initialize currentFormats for initial toolbar state
  try {
    const initRange = quill.getSelection();
    currentFormats.value = initRange ? { ...quill.getFormat(initRange) } : { ...quill.getFormat() };
  } catch (e) {
    currentFormats.value = {};
  }

  if (props.focusOn && !props.notFocusOnInit) {
    focusOnEditor();
  }
  applyStyles();
  // Listen to recording result or cancellation
  eventBus.$on('attach-file-to-active', (file) => {
    if (file) recordigFinished(file);
  });
  setupQuillHandlers();
  // outside-click guards moved to EditorInput
  if (props.initImgArray?.length) insertInitialImages();
  if (props.value) {
    if (typeof props.value === 'string') {
      setContents(convertValue);
      textInit = quill.getText().trim();
    } else {
      setContents(props.value);
      textInit = quill.getText().trim();
    }
  }
});



onUnmounted(() => {
  const emojiClose = document.querySelector('#emoji-close-div');
  if (emojiClose) emojiClose.remove();
  quill.root.removeEventListener('keydown', quillKeyDownHandler);
  quill = null;
});

// --- HELPER FUNCTIONS --- //

function handleFormatToggle(val, old) {
  if (!val && old) {
    focusOnEditor();
    selectEvent.value = null;
    toolbar.value.classList.remove('float-toolbar');
    clearToolbarStyle();
    getEditorElm().style.paddingRight = '';
  } else {
    focusOnEditor();
    nextTick(() => {
      defineEditorPadRight();
      if (selectionPos.value && selectEvent.value) {
        nextTick(() => defineQuillFloatToolbarPos(selectEvent.value));
      }
    });
  }
}

function applyStyles() {
  getEditorElm().style.maxHeight = props.maxHeight;
  if (props.neverScroll) getEditorElm().style.overflowY = 'hidden';
}

function setupQuillHandlers() {
  quill.on('text-change', async () => {

    // instantly start code blocks (quill-markdown-shortcuts has this feature inside, but it require pressing enter or adding some char, this code 
    // starts code block on last from 3 backticks)
    const range = quill.getSelection();
    if (range) {
      const [line, offset] = quill.getLine(range.index);
      const text = line.domNode.textContent;
      if (text.endsWith('```')) {
        const index = range.index - 3;
        // Remove the triple backtick
        quill.deleteText(index, 3);
        // Format the current line as code-block
        quill.formatLine(index, 1, { 'code-block': true });
      }
    }

    text.value = quill.getText().trim();
    emit('input', getSubmitContents());
    if (text.value.trim()) emit('changed', true);
  });

  quill.clipboard.addMatcher(Node.ELEMENT_NODE, (_, delta) => {
    const allowed = ['link', 'list', 'code', 'code-block'];
    delta.ops = delta.ops.map(op => {
      if (op.attributes) {
        Object.keys(op.attributes).forEach(k => {
          if (!allowed.includes(k)) delete op.attributes[k];
        });
      }
      return op;
    });
    return delta;
  });

  quill.on('selection-change', range => {
    selectionPos.value = range;
    try {
      // Spread to force a new object identity for Vue reactivity
      currentFormats.value = range ? { ...quill.getFormat(range) } : {};
    } catch (e) {
      currentFormats.value = {};
    }
    emit('selection', range);
    if (range) {
      isQuillFocused.value = true;
      emit('focus', true);
      showEmojii.value = false;
    } else {
      isQuillFocused.value = false;
      emit('focus', false);
    }
  });

  quill.on('editor-change', (eventName, delta) => {
    if (eventName === 'text-change') {
      const ops = delta.ops;
      const old = quill.getSelection();
      if (ops[0]?.retain === undefined || !ops[1]?.insert || ops[1].insert !== '\n' || old.length > 0) return;
      setTimeout(() => {
        const sel = quill.getSelection();
        if (sel.index === old.index) quill.setSelection(sel.index + 1, 0);
      }, 15);
    }
  });
  // Also keep formats in sync on any text change (caret may move)
  quill.on('text-change', () => {
    const rng = quill.getSelection();
    if (rng) currentFormats.value = { ...quill.getFormat(rng) };
  });
}

function insertInitialImages() {
  props.initImgArray.forEach(img => {
    quill.insertEmbed(0, 'image', img);
    quill.insertText(0, '\n\n\n\n');
  });
}

// DOM helpers
function getEditorElm() { return quill.root; }
function setContents(content) { try { return quill.setContents(content); } catch { return quill.setContents({ ops: [] }); } }

function addKeyBindings() {
  const self = { quill: quill }; // closure

  const codeEnterHandler = (range, context) => {
    const cursorPos = range.index;
    const cursorPosIsAtEnd = cursorPos === quill.getLength() - 1;

    const [blot, offset] = quill.getLine(range.index);
    if (!blot || !blot.domNode || !blot.domNode.closest('.ql-code-block-container')) {
      return true; // not in a code block
    }

    const codeContainer = blot.domNode.closest('.ql-code-block-container');
    const codeLines = [...codeContainer.querySelectorAll('.ql-code-block')];

    // Check if last 3 lines are empty
    const lastThreeLines = codeLines.slice(-3);
    const allEmpty = lastThreeLines.every(line => line.innerText.trim() === '');

    if (allEmpty && cursorPosIsAtEnd) {
      // Exit code block first (this splits the format from now on)
      quill.format('code-block', false);

      // Now remove last 2 empty lines (keep one to act as newline after block)
      const startIndex = cursorPos - 2; // safest bet is remove 2 `\n`
      quill.deleteText(startIndex, 2, 'user');

      // Move cursor after new paragraph
      quill.setSelection(startIndex + 1, 0, 'silent');

      return false;
    }

    return true;
  };
  const shiftEnterBlockquoteHandler = (range, context) => {
    const [line, offset] = quill.getLine(range.index);
    if (!line) return true;

    if (!line.formats().blockquote) return true;

    const prevLine1 = line.prev;
    const prevLine2 = prevLine1?.prev;

    // Check empty blockquote including <br>
    const isEmptyBQLine = l => l && l.formats().blockquote && (l.domNode.innerText || '').trim() === '';

    if (isEmptyBQLine(line) && isEmptyBQLine(prevLine1) && isEmptyBQLine(prevLine2)) {
      const startIndex = range.index - offset 
        - (prevLine1.length() + prevLine2.length());

      quill.deleteText(startIndex, prevLine1.length() + prevLine2.length(), 'user');
      quill.formatLine(startIndex, 1, 'blockquote', false, 'user');
      quill.setSelection(startIndex, 0, 'silent');
      return false;
    }

    // Otherwise insert normal newline inside blockquote
    quill.insertText(range.index, '\n', 'user');
    quill.setSelection(range.index + 1, 0, 'silent');
    return false;
  };
  const bindings = {
    italic: {
      key: 73, // i
      ctrlKey: true,
      handler: () => toggleFormat('italic')
    },
    bold: {
      key: 66, // b
      ctrlKey: true,
      handler: () => toggleFormat('bold')
    },
    code: {
      key: 67, // c
      ctrlKey: true,
      shiftKey: true,
      handler: () => toggleFormat('code')
    },
    codeBlock: {
      key: 219, // [{
      ctrlKey: true,
      handler: () => toggleFormat('code-block')
    },
    link: {
      key: 76, // l
      ctrlKey: true,
      shiftKey: true,
      handler: () => toggleLink()
    },
    orderedList: {
      key: 55, // 7
      ctrlKey: true,
      shiftKey: true,
      handler: () => {
        toggleList('ordered');
      }
    },
    bulletList: {
      key: 56, // 8
      ctrlKey: true,
      shiftKey: true,
      handler: () => {
        toggleList('bullet');
      }
    },
    blockquote: {
      key: 57, // 9
      ctrlKey: true,
      shiftKey: true,
      handler: () => {
        toggleFormat('blockquote');
      }
    },
    backspaceBlockquoteEmpty: {
      key: 'Backspace',
      format: ['blockquote'],
      empty: true,
      handler: () => {
        toggleFormat('blockquote');
      }
    },
    shiftEnterBlockquote: {
      key: 'Enter',
      format: ['blockquote'],
      shiftKey: true,
      handler: shiftEnterBlockquoteHandler
    },
    ctrlEnterBlockquote: {
      key: 'Enter',
      ctrlKey: true,
      format: ['blockquote'],
      handler: shiftEnterBlockquoteHandler
    },
    backspaceCodeBlockEmpty: {
      key: 'Backspace',
      format: ['code-block'],
      empty: true,
      handler: () => {
        toggleFormat('code-block');
      }
    },
    shiftEnterCodeBlock: {
      key: 'Enter',
      format: ['code-block'],
      shiftKey: true,
      handler: codeEnterHandler
    },
    ctrlEnterCodeBlock: {
      key: 'Enter',
      ctrlKey: true,
      format: ['code-block'],
      handler: codeEnterHandler
    },
    enter: {
      key: 'Enter',
      handler: rng => {
        if (props.dontSubmitOnEnter) {
          self.quill.insertText(rng.index, '\n');
          return;
        } else {
          doSubmit();
          return false; // prevent default behavior
        }
      }
    },

    ctrl_enter: {
      key: 'Enter',
      ctrlKey: true,
      handler: rng => quill.insertText(rng.index, '\n')
    },
  };
  if (!props.alwaysOpen) {
    bindings.esc = {
      key: 'Escape',
      handler: () => {
        emit('opened', false);
        return false; // prevent default behavior
      }
    };
  }
  return bindings;
}



function defineEditorPadRight() {
  const w = wrapper.value.getBoundingClientRect();
  const btn = wrapper.value.querySelector('.quill-float-btn');
  const btnW = btn.getBoundingClientRect().width;
  const offset = parseInt(getComputedStyle(container.value).paddingLeft);
  const padRight = w.width - (w.width - btnW) + offset;
  getEditorElm().style.paddingRight = `${padRight}px`;
}

// toolbar positions
function clearToolbarStyle() { toolbar.value.style.left = toolbar.value.style.top = ''; }
function defineQuillFloatToolbarPos({ clientX, clientY }) {
  toolbar.value.classList.add('float-toolbar');
  const contRect = container.value.getBoundingClientRect();
  const offset = parseInt(getComputedStyle(container.value).paddingLeft);
  toolbarData.width = toolbarData.width || toolbar.value.getBoundingClientRect().width;
  toolbarData.height = toolbarData.height || toolbar.value.getBoundingClientRect().height;
  const x = clamp(clientX - contRect.left - toolbarData.width / 2, contRect.left, contRect.right, offset);
  toolbar.value.style.left = `${x}px`;
  toolbar.value.style.top = `${clientY - contRect.top - toolbarData.height}px`;
}
function clamp(pos, left, right, offset) {
  let x = pos;
  if (x + toolbarData.width > right) x = right - toolbarData.width;
  if (x < left + offset) x = left + offset;
  return x;
}

function focusOnEditor() {
  quill.focus();
}


// Events
function selectQuillText(e) { if (window.getSelection().type === 'Range' && e.target.textContent) { selectEvent.value = e; if (isFormatClick.value) defineQuillFloatToolbarPos(e); } }
function onInputFileChange() {
  const files = input.value.files;
  if (!files.length) return;
  if (props.typeOfEditor === 'comment') { emit('commentAttachImage', files); }
  else { attachments.value = Array.from(files); }
}
function mountPreview() {
  const pb = parseInt(getComputedStyle(container.value).paddingBottom);
  container.value.style.paddingBottom = `${pb + preview.value.height + pLCont.value * 2}px`;
  focusOnEditor();
}
function previewEmpty() {
  container.value.style.paddingBottom = '';
  attachments.value = [];
  imageArray.value = [];
}
function onPreviewError({ limit }) {
  store.commit('system/showAlert', {
    message: proxy.$t('task.max_images_reached', { limit }),
    type: 'warning',
    // one notice however many images of the batch did not fit
    alertId: MAX_IMAGES_ALERT_ID,
    timeout: 3,
  });
}
function hasFormat(name, value) {
  const cur = currentFormats.value?.[name];
  if (typeof value !== 'undefined') return cur === value;
  return !!cur;
}
function toggleFormat(name) {
  if (!quill) return;
  const range = quill.getSelection(true);
  if (!range) return;
  const fmt = quill.getFormat(range);
  const next = !fmt[name];
  quill.format(name, next, 'user');
  // Optimistically update cache for instant feedback
  currentFormats.value = { ...fmt, [name]: next };
  quill.focus();
}
function toggleList(type) {
  if (!quill) return;
  const range = quill.getSelection(true);
  if (!range) return;
  const fmt = quill.getFormat(range);
  const next = fmt.list === type ? false : type;
  quill.format('list', next, 'user');
  currentFormats.value = { ...fmt, list: next };
  quill.focus();
}
function toggleLink() {
  if (!quill) return;
  const range = quill.getSelection(true) || { index: Math.max(0, quill.getLength() - 1), length: 0 };
  const fmt = quill.getFormat(range);
  if (fmt.link) {
    const [link, offset] = quill.scroll.descendant(Quill.import('formats/link'), range.index);
    quill.formatText(range.index - offset, link.length(), 'link', false, 'user');
    currentFormats.value = { ...fmt, link: false };
    return;
  }
  linkPromptText.value = range.length > 0 ? quill.getText(range.index, range.length) : '';
  linkPromptRange.value = range;
}

function applyLink({ text, url: rawUrl }) {
  const range = linkPromptRange.value;
  linkPromptRange.value = null;
  if (!range) return;
  let url = (rawUrl || '').trim();
  if (!url) return;
  // Basic normalization
  if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url)) {
    url = 'https://' + url;
  }
  const displayText = (text || '').trim() || url;
  if (range.length > 0 && displayText === quill.getText(range.index, range.length)) {
    quill.formatText(range.index, range.length, 'link', url, 'user');
  } else {
    if (range.length > 0) quill.deleteText(range.index, range.length, 'user');
    quill.insertText(range.index, displayText, { link: url }, 'user');
  }
  // Re-focus the editor; selection-change refreshes the toolbar state.
  quill.setSelection(range.index + displayText.length, 0);
}

function cancelLink() {
  linkPromptRange.value = null;
}
function doSubmit() {
  if (!props.allowSubmit) return;
  const sub = { ...getSubmitContents() };
  if (imageArray.value.length) {
    sub.files = preview.value.createObjSimpleFilesList(imageArray.value);
    emit('submitWithAttach', sub);
    previewEmpty();
  } else {
    emit('submit', sub);
  }
  focusOnEditor();

  quill.setContents({ ops: [] });
}

function onSelectEmojii(event) {
  showEmojii.value = false;
  focusOnEditor();
  const sel = quill.getSelection();
  const chars = [...event.native];
  chars.forEach(ch => {
    quill.insertText(sel.index++, ch, 'user');
  });
  quill.setSelection(sel.index + 1, 0);
}

function emitClick() {
  emit('clicked');
}

// Utility
function getSubmitContents() {
  return {
    delta: quill.getContents(),
    html: formatSubmitEditorHTML(getEditorElm().innerHTML),
    text: quill.getText().trim(),
  };
}
function formatSubmitEditorHTML(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll('p > br').forEach(br => br.remove());

  div.querySelectorAll('ol').forEach(ol => {
    const hasBullet = ol.querySelector('li[data-list="bullet"]');
    if (!hasBullet) return;

    const ul = document.createElement('ul');

    Array.from(ol.children).forEach(li => {
      ul.appendChild(li);
    });

    ol.replaceWith(ul);
  });

  const codeBlocks = div.querySelectorAll('div.ql-code-block-container');
  codeBlocks.forEach((block) => {
    const codeBlock = Array.from(block.querySelectorAll('.ql-code-block')).map(el => el.innerText).join('\n');
    const result = hljs.highlightAuto(codeBlock);
    if (result.language === undefined) {
      return;
    }
    block.classList.add('hljs');
    block.classList.add(`language-${result.language}`);
    block.innerHTML = `<div class="ql-code-block">${result.value}</div>`;

  });

  return div.innerHTML;
}
function onMentionClick() {
  quill.focus();
  const sel = quill.getSelection();
  quill.insertText(sel.index++, '@', 'user');
  quill.setSelection(sel.index + 1, 0);
}

// Unsaved changes helpers
function hasUnsavedChanges() {
  try {
    const current = quill.getText().trim();
    const dirtyText = current.length > 0 && current !== (textInit || '');
    const hasImages = (attachments.value && attachments.value.length) || (imageArray.value && imageArray.value.length);
    return dirtyText || hasImages;
  } catch (e) {
    return false;
  }
}

// expose minimal public API for parent refs
defineExpose({
  doFocus: () => {
    if (quill) quill.focus();
  },
  focusToEnd: () => {
    if (!quill) return;
    quill.focus();
    const len = quill.getLength();
    const index = Math.max(0, len - 1); // place before trailing newline (Quill 2.x)
    quill.setSelection(index, 0, 'silent');
  },
  doBlur: () => {
    if (quill) quill.blur();
  },
  hasFocus: () => {
    return !!(quill && quill.hasFocus());
  },
  hasUnsavedChanges: () => hasUnsavedChanges(),
  dropChanges: () => {
    try { quill.setContents({ ops: [] }); } catch {}
    attachments.value = [];
    imageArray.value = [];
    textInit = '';
    try { previewEmpty(); } catch {}
  },
});
</script>

<style lang="scss">
/* Quill renders its own DOM, so its styles cannot be utility classes. */

.userAvatar.active > :first-child {
  box-shadow: 0 0 0 0.0625rem var(--accent), 0 0 0.25rem 0.125rem var(--accent);
}

.emoji-picker {
  position: absolute;
  right: 0;
  bottom: 3rem;
  z-index: var(--z-popover);
  width: 21.625rem;
}

.fadeIn {
  animation: fadeIn 0.2s;
}

.fadeOut {
  animation: fadeOut 0.2s;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

.ql-editor {
  padding: 0;
  color: var(--text);
  font-family: inherit;

  blockquote {
    margin: 0 0 0 0.25rem;
    padding-left: 0.75rem;
    border-left: 0.25rem solid var(--line);
  }
}

.ql-container {
  font-size: var(--fs-body);
}

.ql-snow.ql-toolbar:after,
.ql-snow .ql-toolbar:after {
  display: none;
}

.ql-editor.ql-blank::before {
  left: 0.75rem;
  right: 0;
  color: var(--text-muted);
  font-style: normal;
}

.ql-snow .ql-hidden {
  display: none;
}

.ql-tooltip {
  display: none !important;
}

/* mention suggestions */
.ql-mention-list {
  margin: 0;
  padding: 0;
  list-style: none;
  overflow: hidden;
}

.mention-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.75rem;
  line-height: 2.375rem;
  font-size: var(--fs-body);
  cursor: pointer;
}

.mention-item:first-child,
.mention-item:hover,
.ql-mention-list-item.selected {
  background-color: var(--hover);
  text-decoration: none;
}

.ql-mention-list-container,
.mention-list {
  position: absolute;
  max-height: 12.5rem;
  border: 0.0625rem solid var(--line);
  border-radius: var(--chip-radius);
  background-color: var(--bg2);
  box-shadow: var(--shadow-popover);
  z-index: var(--z-popover);
  overflow: auto;
}

.ql-mention-list-container {
  width: 12.5rem;
}

.mention-list {
  bottom: 100%;
  width: 100%;
  opacity: 0.2;
  pointer-events: none;
}

.mention-list:not(:hover) > :first-child {
  background-color: var(--hover);
}

.mention {
  margin-right: 0.125rem;
  padding: 0.0625rem 0;
  border-radius: var(--chip-radius);
  color: var(--accent);
  user-select: all;
}
</style>
<style scoped lang='scss'>
/* the toolbar detaches and floats over the selection; JS positions it via inline top/left */
.quill-toolbar.float-toolbar[class] {
  padding: 0 0.25rem;
  border: 0.0625rem solid var(--line);
  border-radius: var(--control-radius);
  background: var(--bg2);
  box-shadow: var(--shadow-popover);
  z-index: calc(var(--z-popover) + 1);
}

/* attachment previews sit in the padding the editor reserves below its text */
.preview-wrapper {
  position: absolute;
  bottom: 3rem;
  left: 0.75rem;
  right: 6.25rem;
  height: 4.625rem;
  padding: 1.25rem 0 0.75rem;
}

.input-wrapper-format-hide {
  .quill-container {
    min-height: 0;
    padding-right: 0;
    padding-bottom: 0;
  }

  .quill-toolbar {
    display: none;
  }

  .quill-float-btn {
    top: 50%;
    bottom: auto;
    transform: translateY(-50%);
  }

  .preview-wrapper {
    right: 9.375rem;
    bottom: 0;
    width: auto;
  }
}

/* attachments row needs the text to clear it — beats the template's pb-7 via [class] */
.input-wrapper-show-attachments-line.input-wrapper-format-hide .quill-container[class] {
  padding-bottom: 5rem;
}

.input-wrapper-show-attachments-line.input-wrapper-format-show .quill-container[class] {
  padding-bottom: 6.25rem;
}

.quill-toolbar.float-toolbar {
  display: flex;
}

@container (max-width: 26.25rem) {
  .hide-mob {
    display: none;
  }
}

@container (max-width: 22.5rem) {
  .hide-narrowest {
    display: none;
  }
}
</style>
